import { logger, useAuth } from '@multiplatform-apps/service-trips';
import type {
  PassengerDetailPermissions,
  PassengerSearchPermissions,
  PermissionKey,
  TripDetailPermissions,
  UserDetailPermissions,
  UserSearchPermissions,
} from '@multiplatform-apps/service-trips';
import { useMemo } from 'react';

import { getRecordValue } from '../utils';
import { ActionPermissions, DevPermissionOverrides, DevPermissionWindow, IndexKey, IndexPermissions } from './types';

const getWindow = (): DevPermissionWindow => window as unknown as DevPermissionWindow;

const DEV_STORAGE_KEY = '__st_action_perms__';
const DEV_STORAGE_KEY_ALL = '__st_permission_overrides__';

/**
 * Module-level constant evaluated at compile time.
 * webpack DefinePlugin replaces `process.env.NODE_ENV` with `"production"` in production builds.
 * Terser then folds `"production" !== "production"` → `false`, propagates via `reduce_vars`,
 * and eliminates ALL dev-only code paths from the bundle.
 *
 * This is the same pattern React uses internally (`__DEV__`).
 * IMPORTANT: Do NOT use `.includes()` or other method calls here — Terser cannot fold them.
 */
const IS_DEV_BUILD = process.env.NODE_ENV !== 'production';

/** @deprecated Use IS_DEV_BUILD constant directly (internal only). Kept for backwards-compat if Phase 7 banner needs it. */
export const isNonProductionBuild = (): boolean => IS_DEV_BUILD;

const ALL_ACTION_PERMISSIONS: ActionPermissions = {
  canDeleteSchedule: true,
  canCancelGeneralAbsence: true,
  canApprovePassengerSchedules: true,
  canApprovePassengerProfile: true,
  canEditPassenger: true,
  canCreateUser: true,
  canAssignUser: true,
  canManageRoles: true,
  canUseAdvancedTripFilters: true,
  canSeeOperationalAttributes: true,
};

const ALL_PASSENGER_DETAIL_PERMISSIONS: PassengerDetailPermissions = {
  canApprovePassengerProfile: true,
  canActivatePassengerProfile: true,
  canCompletePassengerProfile: true,
  canApprovePassengerSchedules: true,
  canCreatePassengerSchedules: true,
  canDeletePassengerSchedule: true,
  canEditPassengerAddresses: true,
  canEditPassengerContacts: true,
  canEditPassengerInfo: true,
  canEditPassengerInstructionToDriver: true,
  canEditPassengerNotes: true,
  canEditPassengerRequirements: true,
  canSeeOperationalAttributes: true,
  canUpdatePassenger: true,
  canViewPassengerInstructionToDriver: true,
  canViewPassengerNotes: true,
};

const ALL_TRIP_DETAIL_PERMISSIONS: TripDetailPermissions = {
  canSeeContactDriverButton: true,
  canSeeFeedbackButton: true,
  canSeeEditTripButton: true,
  canSeeCancelTripButton: true,
  canSeeRebookTripButton: true,
  canSeeShowTripMapButton: true,
};

const ALL_USER_DETAIL_PERMISSIONS: UserDetailPermissions = {
  canEditUser: true,
  canUpdateUser: true,
  canUpdateUsersAssignedPassengers: true,
  canDeleteUser: true,
};

const ALL_OVERRIDES: DevPermissionOverrides = {
  action: ALL_ACTION_PERMISSIONS,
  route: {
    passengersPage: true,
    usersPage: true,
    tripsPage: true,
    profilePage: true,
    absencePage: true,
    notificationsPage: true,
  },
  passengerDetail: ALL_PASSENGER_DETAIL_PERMISSIONS,
  tripDetail: ALL_TRIP_DETAIL_PERMISSIONS,
  userDetail: ALL_USER_DETAIL_PERMISSIONS,
  passengerSearch: { canSeeRegisterPassengerButton: true },
  userSearch: { canCreateUser: true, canUseSearchUsersButton: true, canSeeAssignPassengersButton: true },
};

const VALID_OVERRIDE_KEYS = new Set<string>([
  'action', 'route', 'passengerDetail', 'tripDetail', 'userDetail', 'passengerSearch', 'userSearch',
]);

const isValidOverrideShape = (obj: object): boolean =>
  Object.keys(obj).every((key) => VALID_OVERRIDE_KEYS.has(key));

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const parseStoredRecord = (raw: string): Record<string, unknown> | null => {
  const parsed: unknown = JSON.parse(raw);
  return isPlainRecord(parsed) ? parsed : null;
};

/** Restores the new layered overrides from storage. Returns `true` if a valid record was found and applied. */
const tryRestoreLayeredOverrides = (): boolean => {
  const stored = sessionStorage.getItem(DEV_STORAGE_KEY_ALL);
  if (!stored) return false;

  const parsed = parseStoredRecord(stored);
  if (!parsed || !isValidOverrideShape(parsed)) return false;

  const layered = parsed as DevPermissionOverrides;
  getWindow().__PERMISSION_OVERRIDES__ = layered;
  // Also populate legacy key for backwards compat
  if (layered.action) {
    getWindow().__ACTION_PERMISSION_OVERRIDES__ = layered.action;
  }
  return true;
};

const restoreLegacyOverrides = (): void => {
  const legacyStored = sessionStorage.getItem(DEV_STORAGE_KEY);
  if (!legacyStored) return;

  const parsed = parseStoredRecord(legacyStored);
  if (!parsed) return;

  const overrides = parsed as Partial<ActionPermissions>;
  getWindow().__ACTION_PERMISSION_OVERRIDES__ = overrides;
  getWindow().__PERMISSION_OVERRIDES__ = { action: overrides };
};

const restoreOverrides = (): void => {
  try {
    if (tryRestoreLayeredOverrides()) return;
    restoreLegacyOverrides();
  } catch {
    // Ignore parse errors
  }
};

const installDevHelpers = (): void => {
  if (!IS_DEV_BUILD) return;
  if (getWindow().setPermissions) return;

  restoreOverrides();

  // Legacy backwards-compatible helper
  getWindow().setActionPermissions = (overrides) => {
    if (overrides) {
      const current = getWindow().__PERMISSION_OVERRIDES__ ?? {};
      const merged: DevPermissionOverrides = { ...current, action: overrides };
      getWindow().__PERMISSION_OVERRIDES__ = merged;
      getWindow().__ACTION_PERMISSION_OVERRIDES__ = overrides;
      sessionStorage.setItem(DEV_STORAGE_KEY_ALL, JSON.stringify(merged));
      sessionStorage.setItem(DEV_STORAGE_KEY, JSON.stringify(overrides));
      logger.info('[dev-permissions] Action permissions overridden:', overrides, '\nReload the page or navigate to see changes.');
    } else {
      delete getWindow().__PERMISSION_OVERRIDES__;
      delete getWindow().__ACTION_PERMISSION_OVERRIDES__;
      sessionStorage.removeItem(DEV_STORAGE_KEY_ALL);
      sessionStorage.removeItem(DEV_STORAGE_KEY);
      logger.info('[dev-permissions] All permission overrides cleared.');
    }
  };

  // New unified helper
  getWindow().setPermissions = (overrides) => {
    if (overrides === null) {
      delete getWindow().__PERMISSION_OVERRIDES__;
      delete getWindow().__ACTION_PERMISSION_OVERRIDES__;
      sessionStorage.removeItem(DEV_STORAGE_KEY_ALL);
      sessionStorage.removeItem(DEV_STORAGE_KEY);
      logger.info('[dev-permissions] All permission overrides cleared.');
    } else if (overrides === 'all') {
      getWindow().__PERMISSION_OVERRIDES__ = ALL_OVERRIDES;
      getWindow().__ACTION_PERMISSION_OVERRIDES__ = ALL_OVERRIDES.action;
      sessionStorage.setItem(DEV_STORAGE_KEY_ALL, JSON.stringify(ALL_OVERRIDES));
      logger.info('[dev-permissions] ALL permissions granted (simulating SUPER_USER).', ALL_OVERRIDES);
    } else {
      getWindow().__PERMISSION_OVERRIDES__ = overrides;
      if (overrides.action) {
        getWindow().__ACTION_PERMISSION_OVERRIDES__ = overrides.action;
      }
      sessionStorage.setItem(DEV_STORAGE_KEY_ALL, JSON.stringify(overrides));
      logger.info('[dev-permissions] Permissions overridden:', overrides, '\nReload the page or navigate to see changes.');
    }
  };

  logger.info(
    '[dev-permissions] Dev permission helpers installed.\n' +
    'Usage:\n' +
    '  setPermissions({ action: { canApprovePassengerProfile: true }, passengerDetail: { canUpdatePassenger: true } })\n' +
    '  setPermissions(\'all\')       // grant everything\n' +
    '  setPermissions(null)        // clear all overrides\n' +
    '  setActionPermissions({ canApprovePassengerProfile: true })  // legacy (action layer only)\n' +
    'Layers: action, route, passengerDetail, tripDetail, userDetail, passengerSearch, userSearch',
  );
};

// ---------------------------------------------------------------------------
// Public getters for each layer (used by consumer components)
// ---------------------------------------------------------------------------

const getLayeredOverrides = (): DevPermissionOverrides | undefined => {
  if (!IS_DEV_BUILD) return undefined;
  return getWindow().__PERMISSION_OVERRIDES__;
};

const getDevOverrides = (): Partial<ActionPermissions> | undefined => {
  if (!IS_DEV_BUILD) return undefined;
  return getWindow().__ACTION_PERMISSION_OVERRIDES__;
};

export const isActionPermissionOverridden = (key: keyof ActionPermissions): boolean => {
  if (!IS_DEV_BUILD) return false;
  const overrides = getDevOverrides();
  return !!overrides && getRecordValue(overrides, key) === true;
};

export const getPassengerDetailOverrides = (): Partial<PassengerDetailPermissions> | undefined => {
  if (!IS_DEV_BUILD) return undefined;
  return getLayeredOverrides()?.passengerDetail;
};

export const getTripDetailOverrides = (): Partial<TripDetailPermissions> | undefined => {
  if (!IS_DEV_BUILD) return undefined;
  return getLayeredOverrides()?.tripDetail;
};

export const getUserDetailOverrides = (): Partial<UserDetailPermissions> | undefined => {
  if (!IS_DEV_BUILD) return undefined;
  return getLayeredOverrides()?.userDetail;
};

export const getPassengerSearchOverrides = (): Partial<PassengerSearchPermissions> | undefined => {
  if (!IS_DEV_BUILD) return undefined;
  return getLayeredOverrides()?.passengerSearch;
};

export const getUserSearchOverrides = (): Partial<UserSearchPermissions> | undefined => {
  if (!IS_DEV_BUILD) return undefined;
  return getLayeredOverrides()?.userSearch;
};

export const isRoutePermissionOverridden = (key: PermissionKey): boolean => {
  if (!IS_DEV_BUILD) return false;
  const route = getLayeredOverrides()?.route;
  return !!route && getRecordValue(route, key) === true;
};

// Install at module level so helpers are available in the console immediately.
// IS_DEV_BUILD is a compile-time constant — in production builds it's `false`,
// so Terser eliminates this entire block (and all code only reachable from it).
if (IS_DEV_BUILD) {
  installDevHelpers();
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/** Maps ActionPermissions → V2 IndexPermissions boolean field (name differs only for schedule approval). */
const ACTION_PERMISSION_TO_INDEX_KEY: Record<keyof ActionPermissions, IndexKey> = {
  canDeleteSchedule: 'canDeleteSchedule',
  canCancelGeneralAbsence: 'canCancelGeneralAbsence',
  canApprovePassengerSchedules: 'canApproveSchedule',
  canApprovePassengerProfile: 'canApprovePassengerProfile',
  canEditPassenger: 'canEditPassenger',
  canCreateUser: 'canCreateUser',
  canAssignUser: 'canAssignUser',
  canManageRoles: 'canManageRoles',
  canUseAdvancedTripFilters: 'canUseAdvancedTripFilters',
  canSeeOperationalAttributes: 'canSeeOperationalAttributes',
};

/** Keys that default to `true` (rather than `superFallback`) when indexPermissions is present but the field is unset. */
const DEFAULT_TRUE_WHEN_INDEX_PRESENT: ReadonlySet<keyof ActionPermissions> = new Set(['canDeleteSchedule']);

const resolveActionPermission = (
  key: keyof ActionPermissions,
  indexPermissions: IndexPermissions | undefined,
  superFallback: boolean,
): boolean => {
  if (!indexPermissions) return superFallback;
  const presentDefault = DEFAULT_TRUE_WHEN_INDEX_PRESENT.has(key) ? true : superFallback;
  return indexPermissions[ACTION_PERMISSION_TO_INDEX_KEY[key]] ?? presentDefault;
};

const buildActionPermissions = (
  indexPermissions: IndexPermissions | undefined,
  superFallback: boolean,
): ActionPermissions =>
  (Object.keys(ACTION_PERMISSION_TO_INDEX_KEY) as (keyof ActionPermissions)[]).reduce(
    (acc, key) => {
      acc[key] = resolveActionPermission(key, indexPermissions, superFallback);
      return acc;
    },
    {} as ActionPermissions,
  );

/**
 * Returns action-level permission flags.
 *
 * In development mode, permissions can be overridden from the browser console:
 * ```
 * setPermissions({ action: { canApprovePassengerProfile: true }, passengerDetail: { canUpdatePassenger: true } })
 * setPermissions('all')  // grant everything
 * setActionPermissions({ canDeleteSchedule: true })  // legacy — action layer only
 * ```
 *
 * In production, overrides are completely dead-code eliminated.
 */
export const useActionPermissions = (): ActionPermissions => {
  const { indexPermissions, isSuperUser } = useAuth();

  return useMemo(() => {
    const base = buildActionPermissions(indexPermissions, !!isSuperUser);

    if (IS_DEV_BUILD) {
      const overrides = getDevOverrides();
      if (overrides) {
        return { ...base, ...overrides };
      }
    }

    return base;
  }, [indexPermissions, isSuperUser]);
};
